import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * BulkImportDropzone Component
 *
 * Drag-and-drop file upload zone for CSV/Excel files
 */

interface BulkImportDropzoneProps {
  onFileSelect: (file: File) => void;
  onFileRemove?: () => void;
  selectedFile: File | null;
  error?: string | null;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export const BulkImportDropzone = ({
  onFileSelect,
  onFileRemove,
  selectedFile,
  error,
  disabled = false,
}: BulkImportDropzoneProps) => {
  const { t } = useTranslation('common');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validate file
   */
  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t('bulk_import_file_too_large', { size: formatFileSize(MAX_FILE_SIZE) });
    }

    // Check file type
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      return t('bulk_import_invalid_file_type');
    }

    return null;
  };

  /**
   * Handle file selection
   */
  const handleFile = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      // Error will be shown by parent component
      return;
    }
    onFileSelect(file);
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) {
      handleFile(files[0]);
    }

    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Open file picker
   */
  const openFilePicker = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  /**
   * Remove selected file
   */
  const handleRemove = () => {
    onFileRemove?.();
  };

  // If file is selected, show file preview
  if (selectedFile) {
    return (
      <div className="border-2 border-gray-300 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 p-2 bg-primary-100 rounded">
            <Icon name={ICONS.FILE} size="md" color="text-primary-600" ariaHidden />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500">
              {formatFileSize(selectedFile.size)}
            </p>
          </div>
          {!disabled && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRemove}
              className="flex-shrink-0 flex items-center gap-2"
            >
              <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
              {t('bulk_import_remove')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show dropzone
  return (
    <div>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFilePicker}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200',
          isDragging
            ? 'border-primary-500 bg-primary-50 scale-[1.01] shadow-md'
            : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* Upload Icon */}
        <Icon
          name={ICONS.UPLOAD}
          size="lg"
          color={isDragging ? 'text-primary-500' : 'text-gray-400'}
          className={cn(
            'mx-auto mb-4 transition-all duration-200',
            isDragging && 'scale-110'
          )}
          ariaHidden
        />

        {/* Instructions */}
        <div className="space-y-2">
          <div className="text-sm text-gray-600">
            <button
              type="button"
              className="font-medium text-primary-500 hover:text-primary-600 focus:outline-none focus:underline"
              disabled={disabled}
            >
              {t('bulk_import_upload_file_button')}
            </button>
            <span className="ml-1">{t('bulk_import_or_drag_drop')}</span>
          </div>
          <p className="text-xs text-gray-500">
            {t('bulk_import_accepted_formats')}
          </p>
          <p className="text-xs text-gray-500">
            {t('bulk_import_max_file_size', { size: formatFileSize(MAX_FILE_SIZE) })}
          </p>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleFileInputChange}
          className="hidden"
          disabled={disabled}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon
              name={ICONS.CAUTION}
              size="sm"
              color="text-red-600"
              className="flex-shrink-0 mt-0.5"
              ariaHidden
            />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};
